/**
 * One drag engine for every Learn game.
 *
 *  - Pointer capture on the source element (long-press callouts, fast drags
 *    and finger-leaves-element can no longer strand a piece mid-air).
 *  - 6px activation threshold, so taps stay taps (tap fallback everywhere).
 *  - Position written to a transform on a ref inside requestAnimationFrame —
 *    never through React state — so the ghost tracks the finger at 120Hz.
 *  - Registered drop targets with live hit-testing: the hovered target gets
 *    `hover=true` for its highlight, drop resolves to a target id or null.
 *  - Lift (scale + shadow), snap-into-slot, and spring-return-home animations
 *    via framer-motion `animate`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { animate } from "framer-motion";

const ACTIVATE_PX = 6;

export function useDragEngine({ onDrop, onPickup, reduceMotion = false } = {}) {
  const targets = useRef(new Map()); // id -> { el, data }
  const [hoverId, setHoverId] = useState(null);
  const [ghost, setGhost] = useState(null); // { payload, w, h, render }
  const ghostRef = useRef(null);
  const state = useRef(null); // live drag state
  const rafRef = useRef(0);

  const registerTarget = useCallback((id, el, data) => {
    if (el) targets.current.set(id, { el, data });
    else targets.current.delete(id);
  }, []);

  // Among overlapping targets, the one whose centre is closest to the pointer wins
  // (nested/adjacent slots no longer depend on registration order).
  const hitTest = useCallback((x, y) => {
    let best = null;
    let bestDist = Infinity;
    targets.current.forEach(({ el }, id) => {
      const r = el.getBoundingClientRect();
      if (x < r.left || x > r.right || y < r.top || y > r.bottom) return;
      const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2));
      if (d < bestDist) { bestDist = d; best = id; }
    });
    return best;
  }, []);

  const paint = useCallback(() => {
    rafRef.current = 0;
    const s = state.current;
    const el = ghostRef.current;
    if (!s || !el) return;
    el.style.transform = `translate3d(${s.x - s.ox}px, ${s.y - s.oy}px, 0) scale(${s.lifted ? 1.08 : 1}) rotate(${s.lifted ? -2 : 0}deg)`;
  }, []);

  const schedule = useCallback(() => {
    if (!rafRef.current) rafRef.current = requestAnimationFrame(paint);
  }, [paint]);

  const finish = useCallback(
    (clientX, clientY, cancelled = false) => {
      const s = state.current;
      if (!s) return;
      state.current = null;
      const targetId = !cancelled && s.active ? hitTest(clientX, clientY) : null;
      setHoverId(null);
      const home = s.sourceRect;
      const el = ghostRef.current;
      const settle = () => {
        setGhost(null);
        if (s.sourceEl?.style) { s.sourceEl.style.opacity = ""; s.sourceEl.style.transition = ""; }
      };
      if (!s.active) { settle(); return; }
      const result = onDrop?.({ payload: s.payload, targetId, clientX, clientY, targetData: targetId ? targets.current.get(targetId)?.data : null });
      const accepted = result !== false && targetId !== null;
      if (accepted) {
        // Snap into the target's centre, then let the game re-render the piece there.
        const t = targets.current.get(targetId)?.el?.getBoundingClientRect();
        if (el && t && !reduceMotion) {
          animate(el, { x: t.left + t.width / 2 - s.ox - s.w / 2 + s.ox, y: t.top + t.height / 2 - s.oy - s.h / 2 + s.oy, scale: 0.6, opacity: 0 },
            { duration: 0.22, ease: [0.22, 1, 0.36, 1] }).then(settle);
        } else settle();
      } else if (el && home && !reduceMotion) {
        animate(el, { x: home.left, y: home.top, scale: 1, rotate: 0 }, { type: "spring", stiffness: 520, damping: 34 }).then(settle);
      } else settle();
    },
    [hitTest, onDrop, reduceMotion],
  );

  const onPointerMove = useCallback(
    (e) => {
      const s = state.current;
      if (!s || e.pointerId !== s.pointerId) return;
      const dx = e.clientX - s.startX;
      const dy = e.clientY - s.startY;
      if (!s.active) {
        if (Math.hypot(dx, dy) < ACTIVATE_PX) return;
        s.active = true;
        s.lifted = true;
        // The piece never vanishes: a faint copy stays home while the lifted one follows the finger.
        if (s.sourceEl?.style) { s.sourceEl.style.transition = "opacity 120ms"; s.sourceEl.style.opacity = "0.35"; }
        const clone = s.render ? null : s.sourceEl?.cloneNode?.(true) || null;
        if (clone) {
          clone.style.opacity = "1"; clone.style.transform = "none"; clone.style.margin = "0"; clone.style.pointerEvents = "none";
          clone.style.width = `${s.w}px`; clone.style.height = `${s.h}px`; clone.removeAttribute("id");
        }
        setGhost({ payload: s.payload, w: s.w, h: s.h, render: s.render, clone, left: s.sourceRect.left, top: s.sourceRect.top });
        onPickup?.(s.payload);
      }
      s.x = e.clientX;
      s.y = e.clientY;
      const id = hitTest(e.clientX, e.clientY);
      if (id !== s.hoverId) { s.hoverId = id; setHoverId(id); }
      schedule();
    },
    [hitTest, onPickup, schedule],
  );

  const onPointerUp = useCallback((e) => {
    const s = state.current;
    if (!s || e.pointerId !== s.pointerId) return;
    try { s.sourceEl?.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
    finish(e.clientX, e.clientY, e.type === "pointercancel");
  }, [finish]);

  /**
   * Bind to a draggable: spreads onPointerDown; pass `render` to draw the
   * ghost (defaults to cloning nothing — supply your tile renderer).
   */
  const dragProps = useCallback(
    (payload, render) => ({
      onPointerDown: (e) => {
        if (state.current || (e.pointerType === "mouse" && e.button !== 0)) return;
        const el = e.currentTarget;
        const r = el.getBoundingClientRect();
        try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        state.current = {
          pointerId: e.pointerId, payload, render, sourceEl: el, sourceRect: r,
          startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY,
          ox: e.clientX - r.left, oy: e.clientY - r.top, w: r.width, h: r.height,
          active: false, lifted: false, hoverId: null,
        };
      },
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      style: { touchAction: "none", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" },
    }),
    [onPointerMove, onPointerUp],
  );

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  /** Render this once at the game root: the floating ghost layer. */
  const GhostLayer = useCallback(() => {
    if (!ghost) return null;
    return (
      <div
        ref={(el) => {
          ghostRef.current = el;
          if (el && ghost.clone && !el.hasChildNodes()) el.appendChild(ghost.clone);
          if (el) {
            // The transform carries the absolute position (pointer minus the
            // grab offset inside the tile); left/top stay at 0 so it isn't applied twice.
            const s = state.current;
            const x = s ? s.x - s.ox : ghost.left;
            const y = s ? s.y - s.oy : ghost.top;
            el.style.transform = `translate3d(${x}px, ${y}px, 0) scale(1.08) rotate(-2deg)`;
          }
        }}
        style={{
          position: "fixed", left: 0, top: 0, width: ghost.w, height: ghost.h, zIndex: 100000,
          pointerEvents: "none", willChange: "transform",
          filter: "drop-shadow(0 18px 24px rgba(11,26,51,0.28))",
        }}
      >
        {ghost.render ? ghost.render(ghost.payload) : null}
      </div>
    );
  }, [ghost]);

  return { dragProps, registerTarget, hoverId, dragging: !!ghost, GhostLayer };
}

/** Convenience: ref callback that registers a drop target by id. */
export function useDropTarget(registerTarget, id, data) {
  return useCallback((el) => registerTarget(id, el, data), [registerTarget, id, data]);
}

export default useDragEngine;
